import { taskAPI } from '@/utils/api';
import { runGoodsTaskBackgroundProcessing } from './goodsTaskProcessor';
import { resolveGoodsSourceCropTaskId, buildPersistedGoodsPrintUrlMap } from './goodsPrintUrlResolve';

const kicked = new Set();

/** 后端合成 Worker 不可用时写入的 batchPhase */
export const GOODS_BROWSER_FALLBACK_PHASE = 'browserFallback';

export function isGoodsBrowserFallbackPhase(taskOrPhase) {
  if (taskOrPhase == null) return false;
  if (typeof taskOrPhase === 'string') {
    return taskOrPhase.trim().toLowerCase() === GOODS_BROWSER_FALLBACK_PHASE.toLowerCase();
  }
  const phase = String(
    taskOrPhase.phase
    || taskOrPhase.result?.batchPhase
    || '',
  ).trim().toLowerCase();
  return phase === GOODS_BROWSER_FALLBACK_PHASE.toLowerCase();
}

/**
 * 进度轮询检测到 browserFallback 时：拉详情并用浏览器 Canvas 续跑（幂等）。
 * @returns {Promise<boolean>} 是否已启动浏览器合成
 */
export async function kickGoodsBrowserFallbackIfNeeded(task, {
  imageSpaceImages = [],
  goodsPrintUrlByImageId = {},
  onLocalProgress,
} = {}) {
  const taskId = String(task?.id || '').trim();
  if (!taskId || !isGoodsBrowserFallbackPhase(task)) return false;
  if (kicked.has(taskId)) return false;
  kicked.add(taskId);

  try {
    const res = await taskAPI.getById(taskId);
    const full = res?.task || res;
    if (!full?.id) {
      kicked.delete(taskId);
      return false;
    }
    // 终态则不再兜底
    const status = String(full.status || '').toLowerCase();
    if (['success', 'partial', 'failed', 'canceled', 'cancelled'].includes(status)) {
      return false;
    }
    const forms = (full.goodsForms?.length ? full.goodsForms : full.result?.goodsForms) || [];
    if (!forms.length) {
      kicked.delete(taskId);
      return false;
    }

    const lineageId = resolveGoodsSourceCropTaskId(full);
    const printUrlMap = buildPersistedGoodsPrintUrlMap({
      ...(full.result?.goodsPrintUrlByImageId && typeof full.result.goodsPrintUrlByImageId === 'object'
        ? full.result.goodsPrintUrlByImageId
        : {}),
      ...goodsPrintUrlByImageId,
    }, lineageId);

    const r = full.result && typeof full.result === 'object' ? full.result : {};
    // 清掉 serverWorker，避免列表仍当服务端任务轮询
    await taskAPI.update(taskId, {
      status: 'processing',
      result: {
        ...r,
        goodsForms: forms,
        goodsPrintUrlByImageId: printUrlMap,
        serverWorker: false,
        batchPhase: 'processing',
        queueWaitMessage: '',
        failureHint: r.failureHint || '合成服务不可用，已改浏览器处理',
      },
      goodsForms: forms,
    }).catch(() => {});

    const result = await runGoodsTaskBackgroundProcessing({
      taskId,
      effectiveGoodsForms: forms,
      imageSpaceImages,
      goodsPrintUrlByImageId: printUrlMap,
      fromCropTaskId: lineageId || undefined,
      taskResultBase: {
        ...r,
        serverWorker: false,
        goodsPrintUrlByImageId: printUrlMap,
      },
      hydrateExisting: true,
      onLocalProgress,
    });

    if (!result) {
      // 可能已有浏览器锁在跑；保留 kicked 避免风暴
      return false;
    }
    return true;
  } catch (error) {
    console.warn('[主图创作] 浏览器兜底启动失败', taskId, error);
    kicked.delete(taskId);
    return false;
  }
}

/** 批量检查进度行 / 本地任务，触发浏览器兜底 */
export function kickGoodsBrowserFallbacksFromProgress(rows, options = {}) {
  const list = Array.isArray(rows) ? rows : [];
  list.forEach((row) => {
    if (!isGoodsBrowserFallbackPhase(row)) return;
    void kickGoodsBrowserFallbackIfNeeded(row, options);
  });
}
